import React, { useMemo, useState } from 'react';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import { DonorSchedulingActions } from '@/app/slices/donorScheduling';
import { CommentDialog, TopRevisionsButton } from '@/components';
import { getRevisionCommentDialogConfig } from '@/components/common/revisions-button/revision-comment-dialog-config';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useFormNav } from '@/hooks/useFormNav';
import { useRevisionIncrement } from '@/hooks/useRevisionIncrement';
import { DonorSchedule } from '@/rtk-query.gql';

interface IDonorScheduleFormNotesProps {
  className?: string;
  viewOnly?: boolean;
  revisionComment: string;
  setRevisionComment: (revisionComment: string) => void;
  revisionNumber: {
    previousRevision: string;
    currentRevision: string;
  };
  setRevisionNumber: (revisionNumber: {
    previousRevision: string;
    currentRevision: string;
  }) => void;
}

export const DonorScheduleFormNotes: React.FC<IDonorScheduleFormNotesProps> = ({
  className = '',
  viewOnly = false,
  revisionComment,
  setRevisionComment,
  revisionNumber,
  setRevisionNumber,
}) => {
  const [params, setParams] = useSearchParams();
  const isRevisionMode = params.get('revision') === 'true';
  const {
    ref: formNavRef,
    onKeyDownCapture,
    handleEnterKeyAdvance,
  } = useFormNav<HTMLDivElement>({
    selectors: [
      'input:not([type="hidden"]):not([disabled]):not([readonly])',
      'select:not([disabled])',
      '.p-dropdown:not(.p-disabled) input',
      '.p-dropdown:not(.p-disabled) input[role="combobox"]',
      '.p-dropdown:not(.p-disabled) .p-dropdown-label',
      '.p-autocomplete:not(.p-disabled) input',
      '.p-calendar:not(.p-disabled) input',
      '.p-inputmask:not(.p-disabled) input',
      '.p-inputnumber:not(.p-disabled) input',
      '.p-selectbutton:not(.p-disabled) .p-button:first-child',
      '.p-nucleus:not(.p-disabled) .p-button:first-child',
      'textarea:not([disabled]):not([readonly])',
      'button:not([disabled])[tabindex]',
    ],
    wrap: true,
    sortByTabIndex: true,
    allowDropdownEnterSelect: true,
  });
  const dispatch = useDispatch();
  const { workingDonorSchedule } = useAppSelector(
    (state) => state.donorScheduling,
  );
  const { getNextRevisionNumber } = useRevisionIncrement(
    workingDonorSchedule?.date,
    workingDonorSchedule?.fertilizationSiteId,
  );

  const [showAddRevisionCommentsDialog, setShowAddRevisionCommentsDialog] =
    useState(false);

  const isRevisionOnSplitEnabled = useFeatureFlag('DS_Revision_on_Split');
  const isMultiSelectEnabled = useFeatureFlag(
    'Select_Multiple_Chute_Schedules',
  );

  const revisionCommentDialogConfig =
    getRevisionCommentDialogConfig(isMultiSelectEnabled);

  const handleRevisionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newRevision = e.target.value;
    if (
      isRevisionOnSplitEnabled &&
      newRevision !== '' &&
      !/^\d+$/.test(newRevision)
    ) {
      return;
    }
    setRevisionNumber({
      previousRevision: revisionNumber.previousRevision,
      currentRevision: newRevision,
    });
    dispatch({
      type: DonorSchedulingActions.UPDATE_WORKING_DONOR_SCHEDULE,
      payload: {
        ...workingDonorSchedule,
        revision: newRevision,
      } as DonorSchedule,
    });
  };

  const handleRevisionCommentChange = (value: string) => {
    setRevisionComment(value);
    dispatch({
      type: DonorSchedulingActions.UPDATE_REVISION_COMMENT,
      payload: {
        revisionComment: value,
      } as DonorSchedule,
    });
  };

  const handleGetRevisionComment = () => {
    const value = workingDonorSchedule?.revisionComment || null;
    return value;
  };

  const handleSetRevisionComment = (_fieldName: string, value: string) => {
    handleRevisionCommentChange(value);
  };

  const suggestedNextRevision = useMemo(() => {
    if (getNextRevisionNumber === null) {
      return null;
    }
    return String(getNextRevisionNumber);
  }, [getNextRevisionNumber]);

  return (
    <div
      className={`donor-schedule-form-general ${className}`.trim()}
      onKeyDownCapture={(e) => {
        onKeyDownCapture(e);
        handleEnterKeyAdvance(e);
      }}
      ref={formNavRef}
    >
      <div className="label-input-column">
        <div className="flex items-end gap-2">
          <div className="label-input-group">
            <label>Revision #</label>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <InputText
                  value={revisionNumber.currentRevision || ''}
                  onChange={handleRevisionChange}
                  disabled={viewOnly || !isRevisionMode}
                  tabIndex={1}
                  className={isRevisionMode ? 'border-yellow-500' : ''}
                />
              </div>
              {!viewOnly && suggestedNextRevision && (
                <div className="text-sm text-gray-500">
                  Next:{' '}
                  <span className="font-semibold">{suggestedNextRevision}</span>
                </div>
              )}
            </div>
          </div>
          <TopRevisionsButton
            icon="pi pi-eye"
            type="button"
            tooltip="Show All Top Revision Numbers Per Fertilization Site"
            date={workingDonorSchedule?.date}
          />
        </div>

        <div className="w-full">
          <label htmlFor="comments">Comments:</label>
          <div className="p-inputgroup">
            <InputTextarea
              value={revisionComment}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                handleRevisionCommentChange(e.target.value)
              }
              rows={3}
              disabled={viewOnly || !isRevisionMode}
              className={`h-full w-full ${isRevisionMode ? 'border-yellow-500' : ''}`}
              tabIndex={2}
            />

            <Button
              icon="pi pi-plus"
              type="button"
              onClick={() => setShowAddRevisionCommentsDialog(true)}
              disabled={viewOnly || !isRevisionMode}
              className="add-comments"
            />
          </div>
        </div>
        <Dialog
          visible={showAddRevisionCommentsDialog}
          onHide={() => setShowAddRevisionCommentsDialog(false)}
          className="w-[80vw]"
        >
          <CommentDialog
            config={revisionCommentDialogConfig}
            setValue={handleSetRevisionComment}
            getValue={handleGetRevisionComment}
            onClose={() => setShowAddRevisionCommentsDialog(false)}
          />
        </Dialog>
      </div>
    </div>
  );
};
